import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressBookingsReaderV2BookingsReaderUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    _: [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    'editor._base_domain': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
      {
        srcPath: '/bookings/bookings-reader',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/bookings-reader',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/bookings/reader/extendedBookings/v2/extended-bookings',
        destPath: '/v2/extended-bookings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_bookings_extended-bookings';

/**
 * > **Deprecation Notice**
 * >
 * > **This endpoint has been replaced with [Query Extended Bookings](https://dev.wix.com/api/rest/wix-bookings/bookings-reader-v2/query-extended-bookings) and will be removed on May 31, 2025.**
 * > **If your app uses this endpoint, we recommend updating your code as soon as possible.**
 *
 *
 * Retrieves a list of bookings, given the provided paging, filtering, and sorting.
 *
 *
 * You can also retrieve information about which actions the customer can perform
 * for the bookings. To do so, pass `withBookingAllowedActions` as `true`.
 *
 * Query Bookings runs with these defaults:
 *
 * - `createdDate` sorted in `DESC` order
 * - `cursorPaging.limit` is `50`
 *
 * `query.fields` and `query.fieldsets` aren't supported for this endpoint.
 *
 * When using filters for dates, you must use [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * Bookings belonging to a schedule don't have a `sessionId`. Therefore you
 * must use the `sessionId` filter that isn't part of the `query` object to
 * filter bookings for courses.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 *
 * When calling Query Bookings, the retrieved data may not contain your most recent changes. See
 * [Wix-data and Eventual Consistency](https://dev.wix.com/docs/api-reference/business-solutions/cms/eventual-consistency)
 * for more information.
 * @deprecated
 */
export function query(payload: object): RequestOptionsFactory<any> {
  function __query({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.reader.v2.extended_booking',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.bookings.reader.v2.BookingsReader.Query',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsReaderV2BookingsReaderUrl({
        protoPath: '/v2/extended-booking/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'extendedBookings.booking.createdDate' },
              { path: 'extendedBookings.booking.startDate' },
              { path: 'extendedBookings.booking.endDate' },
              { path: 'extendedBookings.booking.updatedDate' },
              { path: 'extendedBookings.booking.canceledDate' },
              { path: 'extendedBookings.order.updatedDate' },
              {
                path: 'extendedBookings.transactions.chargedMembershipPaymentDetails.expirationDate',
              },
              { path: 'extendedBookings.subscriptionInfo.cancellationDate' },
              { path: 'extendedBookings.subscriptionInfo.nextBillingDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'extendedBookings.booking.contactDetails.fullAddress.geocode.latitude',
              },
              {
                path: 'extendedBookings.booking.contactDetails.fullAddress.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __query;
}

/**
 * Retrieves a list of bookings, including additional extended information, given the provided paging, filtering, and sorting.
 *
 * `queryExtendedBookings()` doesn't use a query builder, instead it uses [API query language](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language#with-the-api-query-language).
 *
 * Up to 100 extended bookings can be returned per request.
 *
 * `queryExtendedBookings()` runs with these defaults, which you can override:
 *
 * - `createdDate` sorted in `DESC` order
 * - `cursorPaging.limit` is `50`
 *
 *
 * You can retrieve information about which actions the customer can perform
 * for the bookings. To do so, pass `withBookingAllowedActions` as `true`.
 *
 *
 * For field support, see
 * [supported filters](https://www.wix.com/velo/reference/wix-bookings-v2/extendedbookings/supported-filters)
 * for more information.
 *
 * You can specify a filter only once per query. If you specify a filter
 * more than once, only the first filter determines the extended bookings that are returned.
 *
 * When filtering by date, you must use [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 */
export function queryExtendedBookings(
  payload: object
): RequestOptionsFactory<any> {
  function __queryExtendedBookings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sort.origin.latitude' },
          { path: 'query.sort.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.bookings.reader.v2.extended_booking',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.reader.v2.BookingsReader.QueryExtendedBookings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsReaderV2BookingsReaderUrl({
        protoPath: '/v2/extended-bookings/query',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'extendedBookings.booking.createdDate' },
              { path: 'extendedBookings.booking.startDate' },
              { path: 'extendedBookings.booking.endDate' },
              { path: 'extendedBookings.booking.updatedDate' },
              { path: 'extendedBookings.booking.canceledDate' },
              { path: 'extendedBookings.order.updatedDate' },
              {
                path: 'extendedBookings.transactions.chargedMembershipPaymentDetails.expirationDate',
              },
              { path: 'extendedBookings.subscriptionInfo.cancellationDate' },
              { path: 'extendedBookings.subscriptionInfo.nextBillingDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'extendedBookings.booking.contactDetails.fullAddress.geocode.latitude',
              },
              {
                path: 'extendedBookings.booking.contactDetails.fullAddress.geocode.longitude',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryExtendedBookings;
}

/**
 * Counts the number of bookings matching the specified filters.
 *
 *
 * See [Supported Filters](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-reader-v2/supported-filters?apiView=SDK) for a list of supported filters.
 */
export function countExtendedBookings(
  payload: object
): RequestOptionsFactory<any> {
  function __countExtendedBookings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.bookings.reader.v2.extended_booking',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.bookings.reader.v2.BookingsReader.CountExtendedBookings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressBookingsReaderV2BookingsReaderUrl({
        protoPath: '/v2/extended-bookings/count',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __countExtendedBookings;
}
